<template>
  <div class="sleeping-overlay">
    <span v-for="star in stars" :key="star.id" class="sleeping-overlay__star" :style="star.style" />

    <div class="sleeping-overlay__scene">
      <div class="sleeping-overlay__floor" />
      <div class="sleeping-overlay__car">
        <div class="sleeping-overlay__shadow" />
        <img v-if="!imageFailed" :src="carUrl" alt="" @error="imageFailed = true" />
        <span class="sleeping-overlay__z sleeping-overlay__z--1">z</span>
        <span class="sleeping-overlay__z sleeping-overlay__z--2">Z</span>
        <span class="sleeping-overlay__z sleeping-overlay__z--3">Z</span>
      </div>
    </div>

    <p v-if="messageLines.length" class="sleeping-overlay__title">{{ messageLines[0] }}</p>
    <p v-if="messageLines.length > 1" class="sleeping-overlay__subtitle">{{ messageLines.slice(1).join('\n') }}</p>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import carUrl from './car.webp'

const props = defineProps({
  message: {
    type: String,
    default: '',
  },
})

const messageLines = computed(() => (props.message ? props.message.split(/\r?\n/) : []))
const imageFailed = ref(false)

// Fixed positions so the sky does not reshuffle on every render
const stars = Array.from({ length: 28 }, (_, id) => ({
  id,
  style: {
    left: `${(id * 37) % 100}%`,
    top: `${(id * 53) % 60}%`,
    animationDelay: `${(id % 7) * 0.45}s`,
    animationDuration: `${2 + (id % 4)}s`,
  },
}))
</script>

<style lang="scss">
.sleeping-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25em;
  padding: 1em;
  overflow: hidden;
  text-align: center;
  font-family: sans-serif;
  color: #fff;
  background: linear-gradient(#0e0504, #2a100d);

  &__star {
    position: absolute;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: #ffd6d0;
    animation: sleeping-twinkle 3s ease-in-out infinite;
  }

  &__scene {
    position: relative;
    width: min(80vw, 640px);
  }

  &__car {
    position: relative;
    left: 6%;
    transform-origin: 50% 100%;

    img {
      position: relative;
      display: block;
      width: 100%;
      max-height: 45vh;
      object-fit: contain;
    }
  }

  // Contact shadow under the tires; tilted because the car's rear sits further back than its front
  &__shadow {
    position: absolute;
    left: 33%;
    bottom: -3%;
    width: 66%;
    height: 10%;
    background: radial-gradient(ellipse at center, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0) 70%);
    filter: blur(4px);
    transform: rotate(-4deg);
  }

  // Soft pavement fading out around the car, behind it
  &__floor {
    position: absolute;
    left: -4%;
    right: -4%;
    bottom: -12%;
    height: 30%;
    background: radial-gradient(ellipse at 62% 45%, rgba(237, 53, 36, 0.4), rgba(237, 53, 36, 0) 55%),
      radial-gradient(ellipse at center, rgba(130, 48, 40, 0.8), rgba(130, 48, 40, 0) 68%);
  }

  &__z {
    position: absolute;
    top: 18%;
    left: 66%;
    font-weight: bold;
    color: #fff;
    opacity: 0;
    text-shadow: 0 0 6px #fff, 0 0 14px #ff3b2a;
    animation: sleeping-float 3.6s ease-out infinite;

    &--1 {
      font-size: 1.2rem;
      animation-delay: 0s;
    }

    &--2 {
      font-size: 1.8rem;
      animation-delay: 1.2s;
    }

    &--3 {
      font-size: 2.4rem;
      animation-delay: 2.4s;
    }
  }

  &__title {
    margin: 0.9em 0 0;
    max-width: 90vw;
    font-size: 3rem;
    font-weight: bold;
    overflow-wrap: anywhere;
    white-space: pre-line;
    text-shadow: 0 0 6px #fff, 0 0 14px #ff3b2a, 0 0 32px #ed3524;
  }

  &__subtitle {
    margin: 0.5em 0 0;
    max-width: 90vw;
    font-size: 1.1rem;
    opacity: 0.8;
    overflow-wrap: anywhere;
    white-space: pre-line;
  }
}

@media (max-width: 600px), (max-height: 600px) {
  .sleeping-overlay {
    &__title {
      font-size: 2.25rem;
    }

    &__subtitle {
      font-size: 1rem;
    }
  }
}

@media (max-width: 380px) {
  .sleeping-overlay {
    &__title {
      font-size: 1.75rem;
    }

    &__subtitle {
      font-size: 0.9rem;
    }
  }
}

@keyframes sleeping-twinkle {
  0%,
  100% {
    opacity: 0.2;
  }
  50% {
    opacity: 1;
  }
}

@keyframes sleeping-float {
  0% {
    opacity: 0;
    transform: translate(0, 0) rotate(-8deg);
  }
  20% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(30px, -90px) rotate(12deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sleeping-overlay * {
    animation: none !important;
  }
}
</style>
